import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
  FaCog,
  FaClipboard,
  FaClock,
  FaUpload,
} from "../utils/icons";
import { useAuth } from "../context/AuthContext";
import {
  reviewerCanManageLabeling,
  reviewerHasProjectAccess,
  reviewerHomePath,
} from "../utils/reviewerNav";
import { isDemoViewer, isInternalRole } from "../services/roles";
import { decodeToken } from "../utils/tokenUtils";

export const Navbar: React.FC = () => {
  const { isAuthenticated, logout, isAdmin, userType, token } = useAuth();
  // Upload is internal-or-granted: internal staff always, external users only
  // with the `upload` capability (JWT claim). Reviewers lack both, so this also
  // subsumes the old reviewer exclusion.
  const canUpload =
    isInternalRole(userType) ||
    Boolean(token && decodeToken(token)?.capabilities?.includes("upload"));
  const canAccessLabeling =
    isAuthenticated &&
    !isDemoViewer(userType) &&
    (userType !== "reviewer" || reviewerHasProjectAccess() || reviewerCanManageLabeling());
  const navigate = useNavigate();
  const location = useLocation();
  const worklistParams = new URLSearchParams();
  // Return to the reader's current list, including cohorts opened by a shared link.
  if (
    location.pathname === "/worklist" ||
    location.pathname.startsWith("/case-viewer/") ||
    location.pathname.startsWith("/reporter/")
  ) {
    const currentParams = new URLSearchParams(location.search);
    for (const key of ["cohort", "collection"]) {
      const value = currentParams.get(key);
      if (value) worklistParams.set(key, value);
    }
  }
  const worklistPath = `/worklist${worklistParams.size ? `?${worklistParams}` : ""}`;
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="h-12 px-4 glass glass-border sticky top-0 z-50">
      <div className="h-full flex items-center justify-between">
        {/* Left: Logo & Brand */}
        <div className="flex items-center gap-5">
          <Link to="/" className="group flex items-center">
            <img
              src="/images/a2z-logo-linear-dark.png"
              alt="a2z Radiology AI"
              className="h-7 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Nav links are scoped to routes the current role can actually visit. */}
          <div className="flex items-center">
            {userType === "reviewer" ? (
              <Link
                to={reviewerHomePath() || "/login"}
                className="hidden sm:block px-3 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
              >
                Cases
              </Link>
            ) : userType !== "annotator" && !isDemoViewer(userType) ? (
              <Link
                to={worklistPath}
                className="hidden sm:block px-3 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
              >
                Worklist
              </Link>
            ) : null}
            {canAccessLabeling && (
              <Link
                to="/labeling-overview"
                className="px-3 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
              >
                Labeling
              </Link>
            )}
            {/* /upload is internal-or-granted (see canUpload). Hidden for anyone
                who can't upload — gate parity with the route + backend. */}
            {canUpload && (
              <Link
                to="/upload"
                className="hidden sm:block px-3 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
              >
                Uploads
              </Link>
            )}
          </div>
        </div>

        {/* Right: User Actions */}
        <div className="flex items-center gap-2">
          {isAuthenticated && (
            <>
              {/* Admin Menu */}
              {isAdmin && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowAdminMenu(!showAdminMenu)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
                  >
                    <FaCog className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Admin</span>
                    <FaChevronDown
                      className={`w-2.5 h-2.5 transition-transform ${showAdminMenu ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {showAdminMenu && (
                    <div className="absolute right-0 mt-1.5 w-44 py-1 bg-bg-2/95 backdrop-blur-xs border border-border-strong/50 rounded-lg shadow-xl shadow-black/30 z-50">
                      <Link
                        to="/admin/content-library"
                        onClick={() => setShowAdminMenu(false)}
                        className="block px-3 py-2 text-xs text-fg-muted hover:text-white"
                      >
                        Content Library
                      </Link>
                      <button
                        onClick={() => {
                          setShowAdminMenu(false);
                          navigate("/admin/users");
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-fg-muted hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FaUser className="w-3 h-3" />
                        Manage Users
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminMenu(false);
                          navigate("/admin/companies");
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-fg-muted hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FaBuilding className="w-3 h-3" />
                        Companies
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminMenu(false);
                          navigate("/admin/upload_status");
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-fg-muted hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FaUpload className="w-3 h-3" />
                        Upload Status
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminMenu(false);
                          navigate("/admin/feedback");
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-fg-muted hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FaClipboard className="w-3 h-3" />
                        Feedback
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminMenu(false);
                          navigate("/admin/reader-study");
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-fg-muted hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FaClock className="w-3 h-3" />
                        Reader Study
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Sign Out */}
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-fg-muted hover:text-white hover:bg-white/5 rounded-md transition-all"
              >
                <FaSignOutAlt className="w-3 h-3" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};
